/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { useNavigate } from 'js/lib/useRouter';
import { formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Hidden, Skeleton, Typography2, breakpoints } from '@coursera/cds-core';
import { Button as TrackedButton, Link as TrackedLink } from '@coursera/event-pulse/react';

import Help from 'bundles/enterprise-admin-home/components/Help';
import { useEnterpriseAdminTrackingData } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseAdminTrackingData';
import EnterprisePermissionsRequired from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsRequired';
import {
  ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN,
  ENTERPRISE_ADMIN_PERMISSION_VERB,
} from 'bundles/enterprise-admin-roles/types';
import type { FunnelProps } from 'bundles/enterprise-ui/components';
import { Funnel, Metric } from 'bundles/enterprise-ui/components/Funnel';
import { formatNumber } from 'bundles/enterprise-ui/utils';
import TrackedDivV2 from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/enterprise-admin-home';

export const mapUsageSummaryToUsageOverviewProps = (usageSummary?: {
  totalInvitationCount?: number;
  totalLearnersJoined?: number;
  totalLearnersNotJoined?: number;
  totalLearnersWithEnrollments?: number;
  totalLearnersWithoutEnrollments?: number;
  remainingLicenseLimit?: number | null;
  totalLicenseLimit?: number | null;
  remainingEnrollmentLimit?: number | null;
  totalEnrollmentLimit?: number | null;
}) => {
  const {
    totalInvitationCount: invitationsSent,
    totalLearnersJoined: learnersJoined,
    totalLearnersNotJoined: notJoined,
    totalLearnersWithEnrollments: enrolledLearners,
    totalLearnersWithoutEnrollments: notEnrolledLearners,
    remainingLicenseLimit,
    totalLicenseLimit,
    remainingEnrollmentLimit,
    totalEnrollmentLimit,
  } = usageSummary || {};
  const remainingLicenses = totalLicenseLimit
    ? `${formatNumber(remainingLicenseLimit)} / ${formatNumber(totalLicenseLimit)}`
    : '';
  const remainingEnrollments = totalEnrollmentLimit
    ? `${formatNumber(remainingEnrollmentLimit)} / ${formatNumber(totalEnrollmentLimit)}`
    : '';
  const emptyFunnelSize = 4;
  const invitationsFunnelSize = !invitationsSent ? emptyFunnelSize : 100;
  const learnersJoinedFunnelSize =
    !learnersJoined || !invitationsSent ? emptyFunnelSize : (100 * learnersJoined) / invitationsSent;
  const enrolledLearnersFunnelSize =
    !enrolledLearners || !invitationsSent ? emptyFunnelSize : (100 * enrolledLearners) / invitationsSent;

  return {
    invitationsSent,
    learnersJoined,
    enrolledLearners,
    remainingLicenses,
    notJoined,
    remainingEnrollments,
    notEnrolledLearners,

    invitationsFunnelSize,
    learnersJoinedFunnelSize,
    enrolledLearnersFunnelSize,
  };
};

type PrimaryMetricsProps = {
  invitationsSent?: number;
  learnersJoined?: number;
  enrolledLearners?: number;
  manageLearnersLink: string;
  programId?: string;
  thirdPartyOrganizationId?: string;
};
const PrimaryMetrics: React.FC<PrimaryMetricsProps> = ({
  invitationsSent,
  learnersJoined,
  enrolledLearners,
  manageLearnersLink,
  programId,
  thirdPartyOrganizationId,
}) => (
  <div
    css={{
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--cds-spacing-200)',
      [breakpoints.up('sm')]: {
        gap: 0,
        flexDirection: 'row',
      },
    }}
  >
    <Metric>
      <section data-e2e="invitations-sent-section" data-pendo-guide="invitations-sent-section">
        <Help label={_t('Learners Invited')}>
          {_t(
            'The total number of unique emails that have been invited or entered through SSO. This metric is refreshed approximately every 4 hours.'
          )}
        </Help>

        <Typography2 component="p" variant="bodySecondary" color="supportText">
          {_t('We recommend sending twice the amount of invitations as you have purchased licenses.')}
        </Typography2>
        <Typography2
          variant="subtitleLarge"
          component="div"
          css={{
            [breakpoints.up('sm')]: {
              marginBottom: 24,
            },
          }}
        >
          {invitationsSent === undefined ? <Skeleton /> : formatNumber(invitationsSent)}
        </Typography2>
      </section>
    </Metric>
    <Metric>
      <section data-e2e="learners-joined-section" data-pendo-guide="learners-joined-section">
        <Help label={_t('Learners joined')}>
          {programId
            ? _t(
                'The number of current learners that have joined this program. This metric is refreshed approximately every 4 hours.'
              )
            : _t(
                'The number of current learners that have joined at least one program. This metric is refreshed approximately every 4 hours.'
              )}
        </Help>
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          {_t('We recommend sending weekly reminders to accept and join your program.')}
        </Typography2>
        <Typography2 variant="subtitleLarge" component="div">
          {learnersJoined === undefined ? <Skeleton /> : formatNumber(learnersJoined)}
        </Typography2>
      </section>
    </Metric>
    <Metric>
      <section>
        <Help label={_t('Enrolled Learners')}>
          {_t(
            'The number of current learners that have enrolled in at least one course. This metric is refreshed approximately every 4 hours.'
          )}
        </Help>
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          {_t('We recommend sending weekly reminders to enroll.')}
          <EnterprisePermissionsRequired
            domain={programId ? 'PROGRAM' : 'ORGANIZATION'}
            domainId={programId || thirdPartyOrganizationId}
            subdomain={ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.LEARNERS}
            verb={ENTERPRISE_ADMIN_PERMISSION_VERB.MANAGE}
          >
            <div data-track-component="manage_enrolled_learners_here">
              <FormattedHTMLMessage
                message={_t(` Manage your active and inactive learners <a href="{link}" target="_blank">here</a>.`)}
                link={manageLearnersLink}
              />
            </div>
          </EnterprisePermissionsRequired>
        </Typography2>
        {/* 
          I suspect that we have some SSR issues with EnterprisePermissionsRequired above that break emotion,
          not sure what it is, but this div wrapper seems to fix it.
        */}
        <div>
          <Typography2 variant="subtitleLarge" component="div">
            {enrolledLearners === undefined ? <Skeleton /> : formatNumber(enrolledLearners)}
          </Typography2>
        </div>
      </section>
    </Metric>
  </div>
);

type SecondaryMetricsProps = {
  remainingLicenses?: string;
  notJoined?: number;
  remainingEnrollments?: string;
  notEnrolledLearners?: number;
  enrollmentReportLink?: string;
  inviteLearnersLink: string;
  licenceUsageReportLink?: string;
  sendReminderToJoin?: () => void;
  sendReminderToEnroll?: () => void;
  programId?: string;
  thirdPartyOrganizationId?: string;
  hideRemainingLicenses?: boolean;
  hideRemainingEnrollments?: boolean;
};

const SecondaryMetrics = ({
  remainingLicenses,
  notJoined,
  remainingEnrollments,
  notEnrolledLearners,
  enrollmentReportLink,
  inviteLearnersLink,
  licenceUsageReportLink,
  sendReminderToJoin,
  sendReminderToEnroll,
  programId,
  thirdPartyOrganizationId,
  hideRemainingLicenses,
  hideRemainingEnrollments,
}: SecondaryMetricsProps) => {
  const navigate = useNavigate();
  const enterpriseAdminTrackingData = useEnterpriseAdminTrackingData();
  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--cds-spacing-200)',
        [breakpoints.up('sm')]: {
          gap: 0,
          flexDirection: 'row',
        },
      }}
    >
      <Metric>
        {remainingLicenses && !hideRemainingLicenses ? (
          <section data-testid="remaining-licenses-section" data-pendo-guide="remaining-licenses-section">
            <Help label={_t('Remaining licenses')}>
              {_t(
                'The number of purchased user licenses that have yet to be occupied by learners joining a program. This metric is updated on a daily basis.'
              )}
            </Help>
            <Typography2 variant="subtitleLarge" component="div">
              {remainingLicenses === undefined ? <Skeleton /> : remainingLicenses}
            </Typography2>
          </section>
        ) : (
          <Hidden xsDown>
            <div
              css={css`
                height: 80px;
              `}
            />
          </Hidden>
        )}
        {remainingEnrollments && !hideRemainingEnrollments && (
          <section data-testid="remaining-enrollment-section" data-pendo-guide="remaining-enrollment-section">
            <Help label={_t('Remaining enrollments')}>
              {_t('The total number of remaining purchased enrollments available.')}
            </Help>
            <Typography2 variant="subtitleLarge" component="div">
              {remainingEnrollments === undefined ? <Skeleton /> : remainingEnrollments}
            </Typography2>
          </section>
        )}
        <section>
          <EnterprisePermissionsRequired
            domain={programId ? 'PROGRAM' : 'ANY_PROGRAM'}
            domainId={programId ?? undefined}
            subdomain={ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.LEARNERS}
            verb={ENTERPRISE_ADMIN_PERMISSION_VERB.MANAGE}
          >
            <TrackedDivV2 trackingName="invite_learners" trackingData={{ thirdPartyOrganizationId }}>
              <TrackedButton
                onClick={() => navigate(inviteLearnersLink)}
                trackingData={{
                  button: { name: 'enterprise_start_invite_learner_flow' },
                  pageSection: {
                    sectionName: 'learner_engagement',
                  },
                  ...enterpriseAdminTrackingData,
                }}
                variant="primary"
                size="small"
                data-pendo-guide="invite-learners"
                data-e2e="invite-learners"
              >
                {_t('Invite learners')}
              </TrackedButton>
            </TrackedDivV2>
          </EnterprisePermissionsRequired>
          {licenceUsageReportLink && (
            <div css={{ marginTop: '18px' }}>
              <TrackedLink
                href={licenceUsageReportLink}
                data-testid="utilization-link"
                typographyVariant="bodyPrimary"
                variant="quiet"
                trackingData={{
                  button: { name: 'enterprise_admin_explore_license_usage_dashboard' },
                  pageSection: {
                    sectionName: 'learner_engagement',
                  },
                  ...enterpriseAdminTrackingData,
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {_t('Explore licence usage dashboard')}
              </TrackedLink>
            </div>
          )}
        </section>
      </Metric>
      <Metric>
        <section data-e2e="not-joined-section" data-pendo-guide="not-joined-section">
          <Help label={_t('Not Joined')}>
            {programId
              ? _t(
                  'The number of invited learners that have yet to join this program. This metric is refreshed approximately every 4 hours.'
                )
              : _t(
                  'The number of invited learners that have yet to join a program. This metric is refreshed approximately every 4 hours.'
                )}
          </Help>
          <Typography2 variant="subtitleLarge" component="div">
            {notJoined === undefined ? <Skeleton /> : formatNumber(notJoined)}
          </Typography2>
        </section>
        <section>
          <EnterprisePermissionsRequired
            domain={programId ? 'PROGRAM' : 'ANY_PROGRAM'}
            domainId={programId ?? undefined}
            subdomain={ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.COMMUNICATIONS}
            verb={ENTERPRISE_ADMIN_PERMISSION_VERB.MANAGE}
          >
            <TrackedDivV2 trackingName="send_reminder_to_join" trackingData={{ thirdPartyOrganizationId }}>
              <TrackedButton
                variant="secondary"
                size="small"
                onClick={sendReminderToJoin}
                data-testid="send-reminder-to-join"
                data-pendo-guide="send-reminder-to-join"
                trackingData={{
                  button: { name: 'enterprise_admin_send_reminder_to_join' },
                  pageSection: {
                    sectionName: 'learner_engagement',
                  },
                  ...enterpriseAdminTrackingData,
                }}
              >
                {_t('Send reminder to join')}
              </TrackedButton>
            </TrackedDivV2>
          </EnterprisePermissionsRequired>
        </section>
      </Metric>
      <Metric>
        {remainingEnrollments && !remainingLicenses ? (
          <section>
            <Help label={_t('Remaining Enrollments')}>
              {_t('The number of purchased enrollments that have yet to be used. This metric is updated daily.')}
            </Help>
            <Typography2 variant="subtitleLarge" component="div">
              {remainingEnrollments === undefined ? <Skeleton /> : remainingEnrollments}
            </Typography2>
          </section>
        ) : (
          <section data-e2e="not-enrolled-section" data-pendo-guide="not-enrolled-section">
            <Help label={_t('Not Enrolled')}>
              {programId
                ? _t(
                    'The number of current learners that have joined this program but have yet to enroll in a course. This metric is refreshed approximately every 4 hours.'
                  )
                : _t(
                    'The number of current learners that have joined a program but have yet to enroll in a course. This metric is refreshed approximately every 4 hours.'
                  )}
            </Help>
            <Typography2 variant="subtitleLarge" component="div">
              {notEnrolledLearners === undefined ? <Skeleton /> : formatNumber(notEnrolledLearners)}
            </Typography2>
          </section>
        )}
        <section>
          {sendReminderToEnroll && (
            <EnterprisePermissionsRequired
              domain={programId ? 'PROGRAM' : 'ANY_PROGRAM'}
              domainId={programId ?? undefined}
              subdomain={ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.COMMUNICATIONS}
              verb={ENTERPRISE_ADMIN_PERMISSION_VERB.MANAGE}
            >
              <TrackedDivV2 trackingName="send_reminder_to_enroll" trackingData={{ thirdPartyOrganizationId }}>
                <TrackedButton
                  variant="secondary"
                  size="small"
                  onClick={sendReminderToEnroll}
                  data-testid="send-reminder-to-enroll"
                  trackingData={{
                    button: { name: 'enterprise_admin_send_reminder_to_enroll' },
                    pageSection: {
                      sectionName: 'learner_engagement',
                    },
                    ...enterpriseAdminTrackingData,
                  }}
                >
                  {_t('Send reminder to enroll')}
                </TrackedButton>
              </TrackedDivV2>
            </EnterprisePermissionsRequired>
          )}
          {enrollmentReportLink && (
            <div css={{ marginTop: '18px' }}>
              <TrackedLink
                href={enrollmentReportLink}
                data-e2e="view-enrollment-report"
                typographyVariant="bodyPrimary"
                variant="quiet"
                trackingData={{
                  button: { name: 'enterprise_admin_view_enrollment_report' },
                  pageSection: {
                    sectionName: 'learner_engagement',
                  },
                  ...enterpriseAdminTrackingData,
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {_t('View enrollment report')}
              </TrackedLink>
            </div>
          )}
        </section>
      </Metric>
    </div>
  );
};

export type UsageOverviewProps = PrimaryMetricsProps &
  FunnelProps &
  SecondaryMetricsProps & { contractStartDate?: number };

const UsageOverview: React.FC<UsageOverviewProps> = ({
  invitationsSent,
  learnersJoined,
  enrolledLearners,
  manageLearnersLink,
  funnelStages,
  remainingLicenses,
  notJoined,
  remainingEnrollments,
  notEnrolledLearners,
  enrollmentReportLink,
  inviteLearnersLink,
  licenceUsageReportLink,
  sendReminderToJoin,
  sendReminderToEnroll,
  programId,
  thirdPartyOrganizationId,
  contractStartDate,
  hideRemainingLicenses = true,
  hideRemainingEnrollments = true,
}) => {
  return (
    <section
      className="rc-UsageOverview"
      css={css`
        border-radius: 3px;
        overflow: hidden;
        border: 1px solid var(--cds-color-grey-50);

        ${breakpoints.down('xs')} {
          display: flex;
          flex-direction: column;
          gap: var(--cds-spacing-200);
        }
      `}
    >
      <div
        css={css`
          border-bottom: 1px solid var(--cds-color-grey-50);
          padding: var(--cds-spacing-200) var(--cds-spacing-300);
          display: flex;
          justify-content: space-between;
        `}
      >
        <Typography2 variant="subtitleLarge" component="h3">
          {_t('Engagement funnel')}
        </Typography2>
        <Typography2 color="supportText" component="span">
          {_t('Current contract period')}{' '}
          {contractStartDate
            ? _t('(#{startDate}-Present)', {
                startDate: formatDateTimeDisplay(contractStartDate, 'MMM YYYY'),
              })
            : ''}
        </Typography2>
      </div>
      <PrimaryMetrics
        {...{
          invitationsSent,
          learnersJoined,
          enrolledLearners,
          manageLearnersLink,
          programId,
          thirdPartyOrganizationId,
        }}
      />
      <Hidden xsDown>
        <Funnel funnelStages={funnelStages} />
      </Hidden>
      <SecondaryMetrics
        {...{
          remainingLicenses,
          notJoined,
          remainingEnrollments,
          notEnrolledLearners,
          enrollmentReportLink,
          inviteLearnersLink,
          licenceUsageReportLink,
          sendReminderToJoin,
          sendReminderToEnroll,
          programId,
          thirdPartyOrganizationId,
          hideRemainingLicenses,
          hideRemainingEnrollments,
        }}
      />
    </section>
  );
};

export default UsageOverview;
